// src/hooks/careBadgeKeys.ts
/**
 * Local constants for the two care badge hubs.
 *
 * `@actschurch/ccms-sdk@0.0.43` ships `counsellingBadgeKey` / `coursesBadgeKey`, and the copies below are
 * byte-identical to them. They are still declared here deliberately, see the note above the builders.
 *
 * What the SDK genuinely still lacks is `DashboardDomain` covering the care module: it remains the closed
 * union of 20 generic domains, so `new DomainDashboardClient(url, 'accourses', …)` is a **type** error.
 * Not a runtime problem, the client interpolates the domain straight into
 * `${baseUrl}/hubs/dashboard/${domain}`, so the string reaches the live hub either way. `useSidebarCounts`
 * casts at the single point of use.
 */

/** The two care hubs, as the domain segment of their hub URL. */
export const CARE_DASHBOARD = {
  counselling: 'accounselling',
  courses: 'accourses',
} as const;

/**
 * Badge key builders.
 *
 * ⚠️ These duplicate `counsellingBadgeKey` / `coursesBadgeKey`, which `0.0.43` **does** export. They are
 * kept local **on purpose**, and it is not worth "fixing" by importing from the SDK:
 *
 * - The SDK is a single large pre-bundled dependency. Importing a newly added export means the app only
 *   runs once Vite's dep optimizer has re-bundled it. Upgrading the package in place leaves a stale
 *   bundle in `node_modules/.vite`, and the failure is a hard `SyntaxError: does not provide an export
 *   named 'counsellingBadgeKey'` at module load, which white-screens the whole app. `tsc` and
 *   `vite build` both pass, because only the dev server uses that cache.
 * - The payoff for taking that risk is nine lines of string concatenation.
 *
 * They were diffed against the SDK's version at `0.0.48` and are identical, including the request and
 * intake queue keys added there. If the server ever changes
 * the key grammar, change it here and in `_deploy/_newEndPoints/realtime-sidebar-counts.md` §1.2.
 */

/** Keys emitted by the `accounselling` hub, relative to the envelope domain. */
export const counsellingBadgeKey = {
  /** Org-wide cases in one status. Admin only. */
  adminStatus: (statusID: number) => `admin.cases.status.${statusID}`,
  /** Org-wide case total across every status. Admin only. */
  adminTotal: 'admin.cases.total',
  /** Cases assigned to the connected leader, in one status. */
  leaderStatus: (statusID: number) => `leader.cases.status.${statusID}`,
  /** Case total assigned to the connected leader. */
  leaderTotal: 'leader.cases.total',
  /** Cases awaiting the connected leader's accept or decline. Not a status bucket. */
  leaderPending: 'leader.cases.pending',
  /** Public care requests in one status. Admin only. */
  adminRequestStatus: (statusID: number) => `admin.requests.status.${statusID}`,
  /** Public care requests still awaiting triage. Admin only. */
  adminRequestPending: 'admin.requests.pending',
  /** Public care request total across every status. Admin only. */
  adminRequestTotal: 'admin.requests.total',
} as const;

/** Keys emitted by the `accourses` hub, relative to the envelope domain. */
export const coursesBadgeKey = {
  /** Org-wide registrations in one status. Admin only. */
  adminStatus: (statusID: number) => `admin.registrations.status.${statusID}`,
  /** Org-wide registration total. Admin only. */
  adminTotal: 'admin.registrations.total',
  /** Registrations the connected facilitator is assigned to, in one status. */
  facilitatorStatus: (statusID: number) => `facilitator.registrations.status.${statusID}`,
  /** Registration total the connected facilitator is assigned to. */
  facilitatorTotal: 'facilitator.registrations.total',
  /** Public course intakes in one status. Admin only. */
  adminIntakeStatus: (statusID: number) => `admin.intakes.status.${statusID}`,
  /** Public course intakes still awaiting a decision. Admin only. */
  adminIntakePending: 'admin.intakes.pending',
  /** Public course intake total across every status. Admin only. */
  adminIntakeTotal: 'admin.intakes.total',
} as const;

/**
 * Namespaces a server key with its hub domain, the form the client keeps state in.
 * The hubs send keys domain-relative, and both share one flat map on the client.
 */
export const nsBadgeKey = (domain: string, key: string) => `${domain}.${key}`;
